type JwtPayload = {
  email?: string;
  exp?: number;
};

type CognitoAuthResult = {
  IdToken: string;
  AccessToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
};

type CognitoResponse = {
  AuthenticationResult?: CognitoAuthResult;
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  __type?: string;
  message?: string;
};

export type AuthSession = {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  email?: string;
};

export type NewPasswordChallenge = {
  email: string;
  session: string;
};

export type SignInResult =
  | { kind: 'signedIn'; session: AuthSession }
  | { kind: 'newPasswordRequired'; challenge: NewPasswordChallenge };

const sessionStorageKey = 'dnd-character-org-auth-session-v1';

const configuredRegion = (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'us-east-1';
const configuredUserPoolId = (import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined) ?? '';
const configuredClientId = (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined) ?? '';
const explicitAuthEnabled = import.meta.env.VITE_AUTH_ENABLED as string | undefined;

export const authConfig = {
  enabled:
    explicitAuthEnabled === undefined
      ? Boolean(configuredRegion && configuredUserPoolId && configuredClientId)
      : explicitAuthEnabled.toLowerCase() === 'true',
  region: configuredRegion,
  userPoolId: configuredUserPoolId,
  clientId: configuredClientId,
};

function decodeJwtPayload(token: string): JwtPayload {
  const payload = token.split('.')[1];
  if (!payload) return {};
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return JSON.parse(atob(padded)) as JwtPayload;
}

function saveSession(tokens: CognitoAuthResult): AuthSession {
  const payload = decodeJwtPayload(tokens.IdToken);
  const expiresAt = payload.exp ? payload.exp * 1000 : Date.now() + tokens.ExpiresIn * 1000;
  const session: AuthSession = {
    idToken: tokens.IdToken,
    accessToken: tokens.AccessToken,
    refreshToken: tokens.RefreshToken,
    expiresAt,
    email: payload.email,
  };
  localStorage.setItem(sessionStorageKey, JSON.stringify(session));
  return session;
}

function friendlyCognitoError(error: CognitoResponse) {
  const type = String(error.__type ?? '');
  if (type.includes('NotAuthorizedException') || type.includes('UserNotFoundException')) {
    return 'That email and password did not match an active invitation.';
  }
  if (type.includes('CodeMismatchException')) {
    return 'That reset code did not match. Check the email and try again.';
  }
  if (type.includes('ExpiredCodeException')) {
    return 'That reset code expired. Request a new one.';
  }
  if (type.includes('LimitExceededException') || type.includes('TooManyRequestsException')) {
    return 'Too many attempts. Wait a little bit and try again.';
  }
  if (type.includes('InvalidPasswordException')) {
    return error.message ?? 'Use a stronger password and try again.';
  }
  if (type.includes('PasswordResetRequiredException')) {
    return 'This invitation needs a password reset before it can be used.';
  }
  if (type.includes('UserNotConfirmedException')) {
    return 'This invitation has not been confirmed yet.';
  }
  return error.message ?? 'Sign in could not be completed.';
}

async function cognitoRequest<T extends CognitoResponse>(target: string, payload: Record<string, unknown>) {
  if (!authConfig.region || !authConfig.clientId) {
    throw new Error('Invitation sign in is not configured yet.');
  }

  const response = await fetch(`https://cognito-idp.${authConfig.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(payload),
  });

  const data = (await response.json().catch(() => ({}))) as T;
  if (!response.ok) {
    throw new Error(friendlyCognitoError(data));
  }
  return data;
}

export function getStoredSession() {
  const saved = localStorage.getItem(sessionStorageKey);
  if (!saved) return null;

  try {
    const session = JSON.parse(saved) as AuthSession;
    if (!session.idToken || !session.accessToken || session.expiresAt < Date.now() + 60_000) {
      localStorage.removeItem(sessionStorageKey);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(sessionStorageKey);
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !password) {
    throw new Error('Enter your invited email and password.');
  }

  const result = await cognitoRequest<CognitoResponse>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: authConfig.clientId,
    AuthParameters: {
      USERNAME: normalizedEmail,
      PASSWORD: password,
    },
  });

  if (result.AuthenticationResult) {
    return { kind: 'signedIn', session: saveSession(result.AuthenticationResult) };
  }

  if (result.ChallengeName === 'NEW_PASSWORD_REQUIRED' && result.Session) {
    return {
      kind: 'newPasswordRequired',
      challenge: {
        email: normalizedEmail,
        session: result.Session,
      },
    };
  }

  throw new Error('This invitation needs a sign-in step that is not supported yet.');
}

export async function completeNewPassword(challenge: NewPasswordChallenge, newPassword: string) {
  if (newPassword.length < 10) {
    throw new Error('Use at least 10 characters for the new password.');
  }

  const result = await cognitoRequest<CognitoResponse>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: authConfig.clientId,
    Session: challenge.session,
    ChallengeResponses: {
      USERNAME: challenge.email,
      NEW_PASSWORD: newPassword,
    },
  });

  if (!result.AuthenticationResult) {
    throw new Error('The new password could not be saved.');
  }

  return saveSession(result.AuthenticationResult);
}

export async function requestPasswordReset(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error('Enter the email address that received an invitation.');
  }

  await cognitoRequest('ForgotPassword', {
    ClientId: authConfig.clientId,
    Username: normalizedEmail,
  });

  return normalizedEmail;
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedCode = code.trim();

  if (!normalizedEmail || !normalizedCode || !newPassword) {
    throw new Error('Enter your invited email, reset code, and new password.');
  }
  if (newPassword.length < 10) {
    throw new Error('Use at least 10 characters for the new password.');
  }

  await cognitoRequest('ConfirmForgotPassword', {
    ClientId: authConfig.clientId,
    Username: normalizedEmail,
    ConfirmationCode: normalizedCode,
    Password: newPassword,
  });

  return normalizedEmail;
}

export function signOut() {
  const session = getStoredSession();
  localStorage.removeItem(sessionStorageKey);

  if (!session?.accessToken) {
    window.location.reload();
    return;
  }

  void cognitoRequest('GlobalSignOut', { AccessToken: session.accessToken }).finally(() => {
    window.location.reload();
  });
}
